import React from 'react';
import { AdminCrud } from './AdminCrud';
import { SupportBadge } from './AdminStoryMedia';
import { StatusBadge, formatDate } from '../../components/Shared';
import { Badge } from '../../components/ui/badge';
import { FilePlus2 } from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const pub = (v) => (v ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" variant="outline">Published</Badge> : <Badge variant="outline" className="bg-slate-100 text-slate-600">Draft</Badge>);

const SUPPORT_LEVEL_LABELS = {
  'resume-supported': 'Resume-supported',
  'flyer-supported': 'Flyer-supported',
  'steve-provided': 'Steve-provided',
  'document-attached': 'Proof attached',
  'needs-document': 'Needs document',
  'private-source': 'Private source only',
};

// Shared "Spending Claim Safety Check" fields — used by Newsroom + City Data.
// Publishing anything with has_spending_claim ON is blocked server-side unless
// a source is attached and the source status is honest.
const claimFields = (docCategory) => [
  { name: '_claim_heading', label: 'Spending Claim Safety Check', type: 'heading', placeholder: 'Only needed when this item contains a dollar-amount claim. Publishing is blocked unless a source is attached and clearly labeled.' },
  { name: 'has_spending_claim', label: 'This item contains a spending / dollar-amount claim', type: 'switch', default: false, help: 'When ON: no source = cannot publish. Public statement only = publishes with a "reported / not yet verified" label. Official records = can be marked Source-Based or Verified.' },
  { name: 'claim_source_status', label: 'Claim source status', type: 'select', default: 'needs_review', options: [
    { value: 'user_provided', label: 'User Provided' }, { value: 'needs_review', label: 'Needs Review' },
    { value: 'source_based', label: 'Source-Based (official source attached)' }, { value: 'verified', label: 'Verified' },
    { value: 'not_verified', label: 'Not Verified (blocks publishing)' } ] },
  { name: 'claim_amount_reported', label: 'Amount claimed / reported', type: 'text', placeholder: 'e.g. $700,000' },
  { name: 'claim_amount_verified', label: 'Verified amount (leave blank until officially confirmed)', type: 'text' },
  { name: 'claim_source_url', label: 'Public statement / post URL (e.g. Ron Ashmore post)', type: 'text' },
  { name: 'claim_screenshot_url', label: 'Screenshot of the public statement', type: 'image', category: docCategory },
  { name: 'claim_date_posted', label: 'Date posted', type: 'date' },
  { name: 'claim_checked_date', label: 'Source checked date', type: 'date' },
  { name: 'claim_official_source_url', label: 'Official budget / procurement source link', type: 'text' },
  { name: 'claim_official_doc_url', label: 'Official source document upload', type: 'image', category: 'documents' },
  { name: 'claim_budget_line', label: 'Budget line / page number (shown once Verified)', type: 'text' },
  { name: 'claim_verified_date', label: 'Date verified', type: 'date' },
];

export const AdminNewsroom = () => (
  <AdminCrud
    title="Newsroom"
    reviewable
    description="Civic news stories. Drafts are hidden from the public site until published. Articles with spending claims must pass the Spending Claim Safety Check before publishing."
    listEndpoint="/admin/news" createEndpoint="/news" updateEndpoint="/news" deleteEndpoint="/news"
    columns={[
      { key: 'title', label: 'Title' },
      { key: 'category', label: 'Category' },
      { key: 'status', label: 'Status', render: (i) => pub(i.status === 'published') },
      { key: 'published_at', label: 'Published', render: (i) => formatDate(i.published_at) },
    ]}
    fields={[
      { name: 'title', label: 'Headline', type: 'text' },
      { name: 'dek', label: 'Dek (subheadline)', type: 'textarea', rows: 2 },
      { name: 'category', label: 'Category', type: 'select', default: 'campaign', options: [
        { value: 'campaign', label: 'Campaign' }, { value: 'city_hall', label: 'City Hall' }, { value: 'accountability', label: 'Accountability' },
        { value: 'taxes', label: 'Taxes' }, { value: 'taxes_waste', label: 'Taxes & Waste Watch' }, { value: 'savings_watch', label: 'Savings Watch' }, { value: 'ward8', label: 'Ward 8' }, { value: 'community', label: 'Community' }, { value: 'imported', label: 'Imported' }] },
      { name: 'author', label: 'Byline', type: 'text', default: 'Campaign Team' },
      { name: 'image_url', label: 'Story image', type: 'image', category: 'newsroom' },
      { name: 'body_html', label: 'Story body', type: 'richtext' },
      { name: 'chart_blocks', label: 'Chart blocks (rendered after the story body — mobile-safe)', type: 'chartblocks', default: [] },
      { name: 'why_matters', label: '"Why it matters" (shown as a highlight box on the article)', type: 'textarea', rows: 2 },
      { name: 'how_to_respond', label: '"How residents can respond" (action box on the article)', type: 'textarea', rows: 2 },
      { name: 'source_name', label: 'Source name (for imported/curated stories)', type: 'text' },
      { name: 'source_url', label: 'Source link', type: 'text' },
      { name: 'tags', label: 'Tags (one per line)', type: 'list', rows: 2 },
      { name: 'related_dataset_id', label: 'Related City Data chart', type: 'dynamic_select', endpoint: '/admin/datasets', statusKey: 'published', noneLabel: '— None connected —' },
      { name: 'related_meeting_id', label: 'Related Council meeting', type: 'dynamic_select', endpoint: '/admin/meetings', statusKey: 'status', noneLabel: '— None connected —' },
      ...claimFields('newsroom'),
      { name: 'featured', label: 'Top Story (lead position)', type: 'switch', default: false },
      { name: 'status', label: 'Status', type: 'select', default: 'draft', options: [{ value: 'draft', label: 'Draft' }, { value: 'published', label: 'Published' }] },
    ]}
  />
);

export const AdminMeetings = () => (
  <AdminCrud
    title="Council Meetings"
    reviewable
    description="Meeting tracker entries: agendas, minutes, video links, and participation instructions."
    listEndpoint="/admin/meetings" createEndpoint="/meetings" updateEndpoint="/meetings" deleteEndpoint="/meetings"
    columns={[
      { key: 'title', label: 'Title' },
      { key: 'meeting_type', label: 'Type' },
      { key: 'date', label: 'Date' },
      { key: 'status', label: 'Status', render: (i) => pub(i.status === 'published') },
    ]}
    fields={[
      { name: 'title', label: 'Meeting title', type: 'text' },
      { name: 'meeting_type', label: 'Type', type: 'select', default: 'Council', options: [
        { value: 'Council', label: 'Council' }, { value: 'Committee', label: 'Committee of the Whole' }, { value: 'Budget', label: 'Budget' }, { value: 'Planning', label: 'Planning' }, { value: 'Other', label: 'Other' }] },
      { name: 'date', label: 'Date', type: 'date' },
      { name: 'time', label: 'Time', type: 'text', placeholder: '1:00 PM' },
      { name: 'location', label: 'Location', type: 'text' },
      { name: 'agenda_url', label: 'Agenda link', type: 'text' },
      { name: 'agenda_pdf', label: 'Agenda PDF upload (optional)', type: 'image', category: 'documents' },
      { name: 'minutes_url', label: 'Minutes link', type: 'text' },
      { name: 'minutes_pdf', label: 'Minutes PDF upload (optional)', type: 'image', category: 'documents' },
      { name: 'livestream_url', label: 'Livestream link', type: 'text' },
      { name: 'video_url', label: 'Video embed URL (YouTube embed link)', type: 'text' },
      { name: 'deputation_deadline', label: 'Deputation deadline (shown as a public reminder)', type: 'date' },
      { name: 'topics', label: 'Topics (one per line)', type: 'list', rows: 3 },
      { name: 'wards', label: 'Related wards (one per line, e.g. Ward 8)', type: 'list', rows: 2 },
      { name: 'summary_html', label: 'Plain-language summary (Why this matters)', type: 'richtext' },
      { name: 'participation_html', label: 'How to have your say / participation instructions', type: 'richtext' },
      { name: 'important', label: 'Mark important (highlighted)', type: 'switch', default: false },
      { name: 'status', label: 'Status', type: 'select', default: 'published', options: [{ value: 'draft', label: 'Draft' }, { value: 'published', label: 'Published' }] },
    ]}
  />
);

export const AdminCityData = () => {
  const createExplainer = async (item, reload) => {
    try {
      const r = await api.post(`/admin/datasets/${item.id}/create-explainer`);
      toast.success(`Draft explainer created (Needs Review): "${r.data.slug}". Find it in Newsroom to review — it will NOT publish automatically.`, { duration: 8000 });
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create explainer');
    }
  };
  return (
  <AdminCrud
    title="City Data"
    reviewable
    description='Datasets power the public City Data dashboard charts. Always include a source link and verify figures. Use the gold "Create Newsroom Explainer" action to draft a deeper article from any chart.'
    listEndpoint="/admin/datasets" createEndpoint="/datasets" updateEndpoint="/datasets" deleteEndpoint="/datasets"
    rowActions={[{ label: 'Create Newsroom Explainer', icon: <FilePlus2 className="h-4 w-4" />, onClick: createExplainer, testId: 'citydata-create-explainer' }]}
    columns={[
      { key: 'title', label: 'Title' },
      { key: 'category', label: 'Category' },
      { key: 'chart_type', label: 'Chart' },
      { key: 'published', label: 'Visible', render: (i) => pub(i.published) },
    ]}
    fields={[
      { name: 'title', label: 'Dataset / chart title', type: 'text' },
      { name: 'category', label: 'Category', type: 'select', default: 'taxes', options: [
        { value: 'taxes', label: 'Tax History' }, { value: 'budget', label: 'Operating / Capital Budget' }, { value: 'population', label: 'Population' }, { value: 'infrastructure', label: 'Infrastructure Spending' }, { value: 'roads', label: 'Road Projects / Spending' },
        { value: 'waste', label: 'Waste Spending' }, { value: 'water', label: 'Water / Wastewater' }, { value: 'debt', label: 'Debt' }, { value: 'service_requests', label: 'Service Request Statistics' }, { value: 'customer_service', label: 'Customer Service' }, { value: 'concerns', label: 'Top Resident Concerns' }, { value: 'ward_comparison', label: 'Ward-by-Ward Comparison' }] },
      { name: 'chart_type', label: 'Chart type', type: 'select', default: 'bar', options: [
        { value: 'bar', label: 'Bar chart' }, { value: 'stacked_bar', label: 'Stacked bar chart' }, { value: 'line', label: 'Line chart' }, { value: 'timeline', label: 'Timeline chart' }, { value: 'area', label: 'Area chart' }, { value: 'pie', label: 'Pie chart' }, { value: 'doughnut', label: 'Doughnut chart' }, { value: 'table', label: 'Table' }, { value: 'stat_cards', label: 'Stat cards' }, { value: 'comparison_cards', label: 'Comparison cards' }] },
      { name: 'unit', label: 'Unit', type: 'text', placeholder: '%, $M, people…' },
      { name: 'year', label: 'Year (optional)', type: 'text', placeholder: 'e.g. 2025' },
      { name: 'ward', label: 'Ward (optional)', type: 'text', placeholder: 'e.g. Ward 8 or All' },
      { name: 'description', label: 'Plain-language explanation', type: 'textarea' },
      { name: 'notes', label: 'Internal notes (admin only)', type: 'textarea', rows: 2 },
      { name: 'data_points', label: 'Data points', type: 'datapoints' },
      { name: 'source_name', label: 'Source name', type: 'text' },
      { name: 'source_url', label: 'Source link (blank = "Data source needed" shown publicly)', type: 'text' },
      { name: 'last_updated', label: 'Last updated (shown publicly)', type: 'date' },
      { name: '_more_info_heading', label: 'More Info button (deeper explanation)', type: 'heading', placeholder: 'Connect this chart to a Newsroom explainer. The public button only appears once the linked article is PUBLISHED.' },
      { name: 'more_info_show', label: 'Show "More Info" button', type: 'switch', default: false, help: 'Public rule: hidden until the linked Newsroom article is published.' },
      { name: 'more_info_label', label: 'More Info button label', type: 'text', default: 'More Info' },
      { name: 'related_article_slug', label: 'Linked Newsroom article', type: 'dynamic_select', endpoint: '/admin/news', valueKey: 'slug', labelKey: 'title', statusKey: 'status', noneLabel: 'No related article connected yet.', help: 'If no article is connected (or it is still a draft), the public button stays hidden.' },
      { name: 'related_source_doc_url', label: 'Linked source document', type: 'image', category: 'documents' },
      { name: 'related_meeting_id', label: 'Linked Council meeting', type: 'dynamic_select', endpoint: '/admin/meetings', statusKey: 'status', noneLabel: '— None connected —' },
      { name: 'related_report_id', label: 'Linked public report', type: 'dynamic_select', endpoint: '/admin/reports', statusKey: 'status', noneLabel: '— None connected —' },
      { name: 'related_dataset_id', label: 'Linked dataset', type: 'dynamic_select', endpoint: '/admin/datasets', statusKey: 'published', noneLabel: '— None connected —' },
      { name: 'related_hub_category', label: 'Related Ward 8 Public Issue Tracker category (optional)', type: 'text', placeholder: 'e.g. Roads, Customer Service' },
      { name: 'short_explanation', label: 'Short explanation text', type: 'textarea', rows: 2 },
      { name: 'why_matters', label: '"Why this matters" text', type: 'textarea', rows: 2 },
      { name: 'questions_to_ask', label: '"Questions residents should ask" (one per line)', type: 'list', rows: 3 },
      ...claimFields('charts'),
      { name: 'published', label: 'Published (visible on public City Data page)', type: 'switch', default: true },
    ]}
  />
  );
};

export const AdminAlerts = () => (
  <AdminCrud
    title="Alerts"
    description="Active alerts appear as banners across the public site and on the Alerts page."
    listEndpoint="/admin/alerts" createEndpoint="/alerts" updateEndpoint="/alerts" deleteEndpoint="/alerts"
    columns={[
      { key: 'title', label: 'Title' },
      { key: 'severity', label: 'Severity' },
      { key: 'active', label: 'Active', render: (i) => pub(i.active) },
    ]}
    fields={[
      { name: 'title', label: 'Alert title', type: 'text' },
      { name: 'message', label: 'Message', type: 'textarea' },
      { name: 'severity', label: 'Severity', type: 'select', default: 'info', options: [
        { value: 'info', label: 'Info' }, { value: 'warning', label: 'Warning (amber)' }, { value: 'urgent', label: 'Urgent (red — emergencies only)' }] },
      { name: 'active', label: 'Active', type: 'switch', default: true },
      { name: 'starts_at', label: 'Start date (optional)', type: 'date' },
      { name: 'ends_at', label: 'End date (optional)', type: 'date' },
    ]}
  />
);

export const AdminGallery = () => (
  <AdminCrud
    title="Gallery"
    reviewable
    description="Photos, campaign images, and proof archive items. For Beautiful Ward 8, use the drag-and-drop uploader under Beautiful Ward 8 Photos."
    listEndpoint="/admin/gallery" createEndpoint="/gallery" updateEndpoint="/gallery" deleteEndpoint="/gallery"
    columns={[
      { key: 'title', label: 'Title' },
      { key: 'category', label: 'Category' },
      { key: 'published', label: 'Visible', render: (i) => pub(i.published) },
    ]}
    fields={[
      { name: 'title', label: 'Title', type: 'text' },
      { name: 'caption', label: 'Caption', type: 'textarea', rows: 2 },
      { name: 'image_url', label: 'Image', type: 'image', category: 'gallery' },
      { name: 'category', label: 'Category', type: 'select', default: 'campaign', options: [
        { value: 'campaign', label: 'Campaign' }, { value: 'ward8', label: 'Ward 8' }, { value: 'community', label: 'Community' }, { value: 'city_hall', label: 'City Hall' },
        { value: 'infrastructure', label: 'Infrastructure' }, { value: 'family_history', label: 'Family History' }, { value: 'proof_archive', label: 'Proof Archive' }] },
      { name: 'order', label: 'Sort order', type: 'number', default: 99 },
      { name: 'published', label: 'Published', type: 'switch', default: true },
    ]}
  />
);

export const AdminVideos = () => (
  <AdminCrud
    title="Videos"
    reviewable
    description="YouTube/Rumble/Vimeo embeds and uploaded videos for the Video Centre."
    listEndpoint="/admin/videos" createEndpoint="/videos" updateEndpoint="/videos" deleteEndpoint="/videos"
    columns={[
      { key: 'title', label: 'Title' },
      { key: 'platform', label: 'Platform' },
      { key: 'featured', label: 'Featured', render: (i) => (i.featured ? 'Yes' : '') },
      { key: 'published', label: 'Visible', render: (i) => pub(i.published) },
    ]}
    fields={[
      { name: 'title', label: 'Title', type: 'text' },
      { name: 'description', label: 'Description', type: 'textarea', rows: 2 },
      { name: 'embed_url', label: 'Embed URL (e.g. https://www.youtube.com/embed/VIDEO_ID)', type: 'text' },
      { name: 'platform', label: 'Platform', type: 'select', default: 'youtube', options: [
        { value: 'youtube', label: 'YouTube' }, { value: 'rumble', label: 'Rumble' }, { value: 'vimeo', label: 'Vimeo' }, { value: 'upload', label: 'Uploaded file' }] },
      { name: 'thumbnail_url', label: 'Thumbnail image', type: 'image', category: 'videos' },
      { name: 'category', label: 'Category', type: 'text', default: 'campaign' },
      { name: 'featured', label: 'Featured', type: 'switch', default: false },
      { name: 'published', label: 'Published', type: 'switch', default: true },
    ]}
  />
);

export const AdminStories = () => (
  <AdminCrud
    title="Stories"
    reviewable
    description="Steve's Story timeline blocks. Use cautious wording for concerns (e.g. 'Steve has raised concerns about…'). Upload supporting documents where available."
    listEndpoint="/admin/stories" createEndpoint="/stories" updateEndpoint="/stories" deleteEndpoint="/stories"
    columns={[
      { key: 'title', label: 'Title' },
      { key: 'timeline_date', label: 'Timeline' },
      { key: 'order', label: 'Order' },
      { key: 'support_level', label: 'Support', render: (i) => <SupportBadge level={i.support_level} labels={SUPPORT_LEVEL_LABELS} /> },
      { key: 'status', label: 'Status', render: (i) => pub(i.status === 'published') },
    ]}
    fields={[
      { name: 'title', label: 'Story block title', type: 'text' },
      { name: 'timeline_date', label: 'Timeline date/era (optional)', type: 'text', placeholder: 'e.g. 1917, 1960s, 2023' },
      { name: 'body_html', label: 'Story text', type: 'richtext' },
      { name: 'image_url', label: 'Photo / news clipping', type: 'image', category: 'stories' },
      { name: 'image_caption', label: 'Image caption (shown under the photo)', type: 'text' },
      { name: 'image_alt', label: 'Image alt text (accessibility description)', type: 'text' },
      { name: 'image_show', label: 'Show image publicly', type: 'switch', default: true },
      { name: 'image_position', label: 'Image position', type: 'select', default: 'top', options: [
        { value: 'top', label: 'Top (above text)' }, { value: 'left', label: 'Left of text' },
        { value: 'right', label: 'Right of text' }, { value: 'below', label: 'Below text' } ] },
      { name: 'clipping_url', label: 'Extra clipping / scan (framed under the story)', type: 'image', category: 'stories' },
      { name: 'clipping_caption', label: 'Clipping caption', type: 'text' },
      { name: 'doc_url', label: 'Supporting document (PDF)', type: 'image', category: 'documents' },
      { name: 'video_embed', label: 'Video embed URL (optional)', type: 'text' },
      { name: 'link_url', label: 'Related link URL (optional, e.g. /issue-tracker or https://…)', type: 'text' },
      { name: 'link_label', label: 'Related link label', type: 'text', placeholder: 'e.g. See the Issue Tracker' },
      { name: 'links_text', label: 'Tool link chips (optional — one per line: Label | URL)', type: 'textarea', rows: 3, placeholder: 'Ward 8 Public Issue Tracker | /issue-tracker\nCity Data | /city-data' },
      { name: 'source_notes', label: 'Source notes', type: 'textarea', rows: 2 },
      { name: 'support_level', label: 'Support level (admin label)', type: 'select', default: 'steve-provided', options: [
        { value: 'resume-supported', label: 'Resume-supported' },
        { value: 'flyer-supported', label: 'Flyer-supported' },
        { value: 'steve-provided', label: 'Steve-provided' },
        { value: 'document-attached', label: 'Supporting document attached' },
        { value: 'needs-document', label: 'Needs supporting document' },
        { value: 'private-source', label: 'Private source only' } ] },
      { name: 'order', label: 'Sort order', type: 'number', default: 99 },
      { name: 'status', label: 'Status', type: 'select', default: 'draft', options: [{ value: 'draft', label: 'Draft' }, { value: 'published', label: 'Published' }] },
    ]}
  />
);

export const AdminCalendar = () => (
  <AdminCrud
    title="Calendar"
    reviewable
    description="Campaign events and community dates shown on What's Happening."
    listEndpoint="/admin/events" createEndpoint="/events" updateEndpoint="/events" deleteEndpoint="/events"
    columns={[
      { key: 'title', label: 'Title' },
      { key: 'date', label: 'Date' },
      { key: 'type', label: 'Type' },
      { key: 'published', label: 'Visible', render: (i) => pub(i.published) },
    ]}
    fields={[
      { name: 'title', label: 'Event title', type: 'text' },
      { name: 'description', label: 'Description', type: 'textarea', rows: 2 },
      { name: 'date', label: 'Date', type: 'date' },
      { name: 'time', label: 'Time', type: 'text', placeholder: '10:00 AM' },
      { name: 'location', label: 'Location', type: 'text' },
      { name: 'type', label: 'Type', type: 'select', default: 'campaign', options: [{ value: 'campaign', label: 'Campaign' }, { value: 'council', label: 'Council' }, { value: 'community', label: 'Community' }] },
      { name: 'published', label: 'Published', type: 'switch', default: true },
    ]}
  />
);

export const AdminVolunteers = () => (
  <AdminCrud
    title="Volunteers"
    description="Volunteer sign-ups from the public site."
    listEndpoint="/admin/volunteers" createEndpoint="/admin/volunteers" updateEndpoint="/admin/volunteers" deleteEndpoint="/admin/volunteers"
    allowCreate={false}
    searchKeys={['name', 'email']}
    columns={[
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'interests', label: 'Interests', render: (i) => (i.interests || []).join(', ') },
      { key: 'status', label: 'Status' },
      { key: 'created_at', label: 'Signed up', render: (i) => formatDate(i.created_at) },
    ]}
    fields={[
      { name: 'name', label: 'Name', type: 'readonly' },
      { name: 'email', label: 'Email', type: 'readonly' },
      { name: 'phone', label: 'Phone', type: 'readonly' },
      { name: 'availability', label: 'Availability', type: 'readonly' },
      { name: 'message', label: 'Message', type: 'readonly' },
      { name: 'status', label: 'Status', type: 'select', default: 'new', options: [{ value: 'new', label: 'New' }, { value: 'contacted', label: 'Contacted' }, { value: 'active', label: 'Active volunteer' }, { value: 'inactive', label: 'Inactive' }] },
      { name: 'notes', label: 'Team notes', type: 'textarea' },
    ]}
  />
);

export const AdminMessages = () => (
  <AdminCrud
    title="Contact Messages"
    description="Messages from the public Contact form."
    listEndpoint="/admin/contact-messages" createEndpoint="/admin/contact-messages" updateEndpoint="/admin/contact-messages" deleteEndpoint="/admin/contact-messages"
    allowCreate={false}
    searchKeys={['name', 'email', 'subject']}
    columns={[
      { key: 'name', label: 'From' },
      { key: 'subject', label: 'Subject', render: (i) => i.subject || '(no subject)' },
      { key: 'read', label: 'Status', render: (i) => (i.read ? 'Read' : <Badge className="bg-amber-100 text-amber-800 border-amber-200" variant="outline">Unread</Badge>) },
      { key: 'created_at', label: 'Received', render: (i) => formatDate(i.created_at) },
    ]}
    fields={[
      { name: 'name', label: 'From', type: 'readonly' },
      { name: 'email', label: 'Email', type: 'readonly' },
      { name: 'phone', label: 'Phone', type: 'readonly' },
      { name: 'subject', label: 'Subject', type: 'readonly' },
      { name: 'message', label: 'Message', type: 'readonly' },
      { name: 'read', label: 'Mark as read', type: 'switch', default: false },
      { name: 'replied', label: 'Replied', type: 'switch', default: false },
    ]}
  />
);

export const AdminReports = () => (
  <AdminCrud
    title="Reports"
    reviewable
    description="Monthly Ward 8 accountability reports."
    listEndpoint="/admin/reports" createEndpoint="/reports" updateEndpoint="/reports" deleteEndpoint="/reports"
    columns={[
      { key: 'title', label: 'Title' },
      { key: 'period', label: 'Period' },
      { key: 'status', label: 'Status', render: (i) => pub(i.status === 'published') },
    ]}
    fields={[
      { name: 'title', label: 'Report title', type: 'text' },
      { name: 'period', label: 'Period', type: 'text', placeholder: 'e.g. March 2026' },
      { name: 'body_html', label: 'Report content', type: 'richtext' },
      { name: 'pdf_url', label: 'PDF attachment (optional)', type: 'image', category: 'reports' },
      { name: 'status', label: 'Status', type: 'select', default: 'draft', options: [{ value: 'draft', label: 'Draft' }, { value: 'published', label: 'Published' }] },
    ]}
  />
);
